/**
 * ConsolidatedPositionsTable - Sortable positions table with expandable rows
 * Extracted from ConsolidatedHoldings.tsx (TASK-371 decomposition)
 *
 * Renders the full consolidated positions table with sorting,
 * expandable portfolio breakdowns, duplicate/concentration indicators.
 */

import React from 'react';
import { ChevronUp, ChevronDown, Copy, AlertTriangle } from 'lucide-react';
import { ConsolidatedPosition } from '../../../hooks/useClientLevelData';
import Badge from '../atoms/Badge';
import TrendIndicator from '../atoms/TrendIndicator';

export interface ConsolidatedPositionsTableProps {
  positions: ConsolidatedPosition[];
  totalValue: number;
  sortField: keyof ConsolidatedPosition;
  sortDirection: 'asc' | 'desc';
  onSort: (field: keyof ConsolidatedPosition) => void;
  expandedPosition: string | null;
  onExpandToggle: (isin: string) => void;
}

const formatCurrency = (value: number) => {
  if (value >= 1000000) return `CHF ${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `CHF ${(value / 1000).toFixed(0)}K`;
  return `CHF ${value.toLocaleString('de-CH')}`;
};

const getRiskBadge = (risk?: string) => {
  if (!risk) return null;

  switch (risk.toUpperCase()) {
    case 'HIGH':
      return <Badge label="HIGH RISK" variant="error" size="sm" />;
    case 'MEDIUM':
      return <Badge label="MEDIUM" variant="warning" size="sm" />;
    default:
      return <Badge label="LOW" variant="success" size="sm" />;
  }
};

const getSortedPositions = (
  positions: ConsolidatedPosition[],
  sortField: keyof ConsolidatedPosition,
  sortDirection: 'asc' | 'desc'
) => {
  return [...positions].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return 0;
  });
};

const SortIcon: React.FC<{
  field: keyof ConsolidatedPosition;
  sortField: keyof ConsolidatedPosition;
  sortDirection: 'asc' | 'desc';
}> = ({ field, sortField, sortDirection }) => {
  if (sortField !== field) return null;
  return sortDirection === 'asc'
    ? <ChevronUp className="w-4 h-4" />
    : <ChevronDown className="w-4 h-4" />;
};

const ConsolidatedPositionsTable: React.FC<ConsolidatedPositionsTableProps> = ({
  positions,
  totalValue,
  sortField,
  sortDirection,
  onSort,
  expandedPosition,
  onExpandToggle,
}) => {
  const sortedPositions = getSortedPositions(positions, sortField, sortDirection);

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">All Positions (Consolidated)</h3>
        <p className="text-sm text-gray-600 mt-1">
          Showing {positions.length} position{positions.length !== 1 ? 's' : ''} • Total value: {formatCurrency(totalValue)}
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => onSort('name')}
              >
                <div className="flex items-center gap-2">
                  Position
                  <SortIcon field="name" sortField={sortField} sortDirection={sortDirection} />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => onSort('asset_class')}
              >
                <div className="flex items-center gap-2">
                  Asset Class
                  <SortIcon field="asset_class" sortField={sortField} sortDirection={sortDirection} />
                </div>
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => onSort('value')}
              >
                <div className="flex items-center justify-end gap-2">
                  Value
                  <SortIcon field="value" sortField={sortField} sortDirection={sortDirection} />
                </div>
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => onSort('percentOfTotal')}
              >
                <div className="flex items-center justify-end gap-2">
                  % of Total
                  <SortIcon field="percentOfTotal" sortField={sortField} sortDirection={sortDirection} />
                </div>
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => onSort('unrealized_pl_pct')}
              >
                <div className="flex items-center justify-end gap-2">
                  P/L
                  <SortIcon field="unrealized_pl_pct" sortField={sortField} sortDirection={sortDirection} />
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Portfolios
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Risk
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedPositions.map((position) => (
              <React.Fragment key={position.isin}>
                <tr
                  className={`hover:bg-gray-50 transition-colors ${
                    position.isDuplicate ? 'bg-yellow-50/50' : ''
                  } ${
                    position.concentrationRisk === 'HIGH' ? 'border-l-4 border-l-red-500' : ''
                  } ${
                    expandedPosition === position.isin ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => position.breakdown && onExpandToggle(position.isin)}
                  style={{ cursor: position.breakdown ? 'pointer' : 'default' }}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {position.isDuplicate && (
                        <Copy className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                      )}
                      {position.concentrationRisk === 'HIGH' && (
                        <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
                      )}
                      <div>
                        <div className="font-medium text-gray-900">{position.name}</div>
                        <div className="text-xs text-gray-500">{position.isin}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {position.asset_class}
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
                    {formatCurrency(position.value)}
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-right">
                    <span className={position.percentOfTotal >= 10 ? 'text-yellow-700' : 'text-gray-700'}>
                      {position.percentOfTotal.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {position.unrealized_pl !== undefined && position.unrealized_pl_pct !== undefined && (
                      <TrendIndicator
                        value={position.unrealized_pl_pct}
                        showIcon={true}
                        showValue={true}
                        className="text-sm justify-end"
                      />
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {position.portfolios.length} portfolio{position.portfolios.length !== 1 ? 's' : ''}
                    {position.breakdown && (
                      <span className="ml-2 text-blue-600 text-xs">(click to expand)</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {getRiskBadge(position.concentrationRisk)}
                  </td>
                </tr>

                {/* Expanded row showing portfolio breakdown */}
                {expandedPosition === position.isin && position.breakdown && (
                  <tr className="bg-blue-50">
                    <td colSpan={7} className="px-6 py-4">
                      <div className="pl-8">
                        <p className="text-sm font-medium text-gray-700 mb-2">Portfolio Breakdown:</p>
                        <div className="space-y-2">
                          {position.breakdown.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm bg-white rounded px-3 py-2">
                              <span className="text-gray-700">{item.portfolio}</span>
                              <div className="flex items-center gap-4">
                                <span className="font-medium text-gray-900">{formatCurrency(item.value)}</span>
                                <span className="text-gray-600">({item.percentage.toFixed(1)}% of total)</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ConsolidatedPositionsTable;
